// ===========================================
// components/ELibraryApp.js - MAIN COMPONENT
// ===========================================
import React, { useState, useCallback, useEffect } from 'react';
import { Book, LogOut } from 'lucide-react';

// Import all components
import LoginScreen from './LoginScreen';
import LibraryTab from './LibraryTab';
import UploadTab from './UploadTab';
import AdminPanel from './AdminPanel';

// Import hooks
import { useSearchStore } from '../hooks/useSearchStore';
import { useAppState } from '../hooks/useAppState';

// Import constants
import { TABS, SORT_OPTIONS } from '../utils/constants';

const ELibraryApp = () => {
  // Get initial tab from localStorage or default to LIBRARY
  const getInitialTab = () => {
    const savedTab = localStorage.getItem('activeTab');
    return savedTab && Object.values(TABS).includes(savedTab) ? savedTab : TABS.LIBRARY;
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  
  // Save active tab to localStorage whenever it changes
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    localStorage.setItem('activeTab', tab);
  }, []);
  
  // Custom hooks
  const searchStore = useSearchStore();
  const {
    user,
    books,
    filteredBooks,
    sortBy,
    fuse,
    isLoading,
    isBooksLoading,
    booksError,
    setFilteredBooks,
    setSortBy,
    login,
    logout,
    updateBookDownloads,
    approveUser,
    rejectUser,
    refreshBooks
  } = useAppState();

  // Filter and sorting functions
  const applyFilters = useCallback((books) => {
    let filtered = books;

    if (searchStore.selectedFilters.author) {
      filtered = filtered.filter(book => 
        book.author?.toLowerCase().includes(searchStore.selectedFilters.author.toLowerCase())
      );
    }

    if (searchStore.selectedFilters.uploadDate) {
      const dateFilter = searchStore.selectedFilters.uploadDate;
      const now = new Date();
      let cutoffDate;

      switch (dateFilter) {
        case 'today':
          cutoffDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'week':
          cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          cutoffDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
          break;
        case 'year':
          cutoffDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
          break;
        default:
          break;
      }

      if (cutoffDate) {
        filtered = filtered.filter(book => new Date(book.upload_date) >= cutoffDate);
      }
    }

    if (searchStore.selectedFilters.fileSize) {
      const sizeFilter = searchStore.selectedFilters.fileSize;
      filtered = filtered.filter(book => {
        const sizeMB = book.file_size / (1024 * 1024);
        switch (sizeFilter) {
          case 'small': return sizeMB < 10;
          case 'medium': return sizeMB >= 10 && sizeMB < 50;
          case 'large': return sizeMB >= 50;
          default: return true;
        }
      });
    }

    return filtered;
  }, [searchStore.selectedFilters]);

  const applySorting = useCallback((books) => {
    const sorted = [...books];
    
    switch (sortBy) {
      case SORT_OPTIONS.TITLE:
        sorted.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case SORT_OPTIONS.AUTHOR:
        sorted.sort((a, b) => (a.author || '').localeCompare(b.author || ''));
        break;
      case SORT_OPTIONS.DATE_DESC:
        sorted.sort((a, b) => new Date(b.upload_date) - new Date(a.upload_date));
        break;
      case SORT_OPTIONS.DATE_ASC:
        sorted.sort((a, b) => new Date(a.upload_date) - new Date(b.upload_date));
        break;
      case SORT_OPTIONS.DOWNLOADS:
        sorted.sort((a, b) => (b.download_count || 0) - (a.download_count || 0));
        break;
      case SORT_OPTIONS.RELEVANCE:
      default:
        // Keep search relevance order
        break;
    }
    
    return sorted;
  }, [sortBy]);

  // Search functionality
  const handleSearch = useCallback((searchTerm) => {
    let results = books;

    if (searchTerm.trim()) {
      const fuseResults = fuse.search(searchTerm);
      results = fuseResults.map(result => ({
        ...result.item,
        _searchScore: result.score,
        _searchMatches: result.matches
      }));
    }

    // Apply filters
    results = applyFilters(results);
    
    // Apply sorting
    results = applySorting(results);

    setFilteredBooks(results);
  }, [books, fuse, applyFilters, applySorting, setFilteredBooks]);

  // Initialize search when books are loaded
  useEffect(() => {
    if (books.length > 0 && searchStore.searchTerm) {
      handleSearch(searchStore.searchTerm);
    } else if (books.length > 0) {
      // Apply filters and sorting to all books if no search term
      let results = applyFilters(books);
      results = applySorting(results);
      setFilteredBooks(results);
    }
  }, [books, searchStore.searchTerm, handleSearch, applyFilters, applySorting, setFilteredBooks]);

  // Event handlers
  const handleViewBook = useCallback((book) => {
    console.log('Viewing book:', book.title);
  }, []);

  const handleDownloadBook = useCallback((bookId) => {
    updateBookDownloads(bookId);
    console.log('Downloaded book ID:', bookId);
  }, [updateBookDownloads]);

  // Render login screen if not authenticated
  if (!user) {
    return <LoginScreen onLogin={login} />;
  }

  // Show loading screen while initial data is being fetched after login
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full mx-4">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 text-blue-600 mb-4">
              <Book className="w-12 h-12 animate-pulse" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Library...</h2>
            <p className="text-gray-600">Setting up your reading experience</p>
            <div className="mt-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render pending approval screen
  if (!user.is_approved) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full mx-4">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 text-yellow-500 mb-4">⏳</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Pending Approval</h2>
            <p className="text-gray-600">
              Your account is waiting for admin approval. You'll be notified once approved.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Main app interface
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3">
              <Book className="h-8 w-8 text-blue-600" />
              <h1 className="text-xl font-bold text-gray-900">E-Library</h1>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Welcome, {user.first_name}
                {user.is_admin && (
                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                    Admin
                  </span>
                )}
              </span>
              <button
                onClick={logout}
                className="text-gray-500 hover:text-gray-700"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => handleTabChange(TABS.LIBRARY)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === TABS.LIBRARY
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Library
            </button>
            <button
              onClick={() => handleTabChange(TABS.UPLOAD)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === TABS.UPLOAD
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Upload
            </button>
            {user.is_admin && (
              <button
                onClick={() => handleTabChange(TABS.ADMIN)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === TABS.ADMIN
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Admin Panel
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === TABS.LIBRARY && (
          <LibraryTab
            books={books}
            filteredBooks={filteredBooks}
            searchStore={searchStore}
            sortBy={sortBy}
            setSortBy={setSortBy}
            onSearch={handleSearch}
            onViewBook={handleViewBook}
            onDownloadBook={handleDownloadBook}
            onSwitchTab={handleTabChange}
            isBooksLoading={isBooksLoading}
            booksError={booksError}
            refreshBooks={refreshBooks}
          />
        )}

        {activeTab === TABS.UPLOAD && (
          <UploadTab
            user={user}
            onUpload={refreshBooks}
          />
        )}

        {activeTab === TABS.ADMIN && user.is_admin && (
          <AdminPanel
            onApproveUser={approveUser}
            onRejectUser={rejectUser}
          />
        )}
      </div>
    </div>
  );
};

export default ELibraryApp;